import { Box, Link as MuiLink, Tab, Tabs, Typography } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import DashboardIcon from '@mui/icons-material/Dashboard';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

interface BookEngineNavProps {
  bookId: string;
}

const ENGINES = [
  {
    label: 'Editor',
    icon: <EditNoteIcon fontSize="small" />,
    path: '',
    standaloneRoute: null,
  },
  {
    label: 'Outline',
    icon: <AccountTreeIcon fontSize="small" />,
    path: '/outline',
    standaloneRoute: '/outlines',
  },
  {
    label: 'Story Studio',
    icon: <AutoStoriesIcon fontSize="small" />,
    path: '/story-studio',
    standaloneRoute: '/story-studio',
  },
  {
    label: 'Boards',
    icon: <DashboardIcon fontSize="small" />,
    path: '/boards',
    standaloneRoute: '/boards',
  },
  {
    label: 'Consistency',
    icon: <VerifiedIcon fontSize="small" />,
    path: '/consistency',
    standaloneRoute: null,
  },
] as const;

export function BookEngineNav({ bookId }: BookEngineNavProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const basePath = `/books/${bookId}`;
  const currentSuffix = location.pathname.replace(basePath, '') || '';

  const activeIndex = ENGINES.findIndex((e) => {
    if (e.path === '')
      return currentSuffix === '' || currentSuffix === '/resume';
    return currentSuffix.startsWith(e.path);
  });

  const activeEngine = activeIndex >= 0 ? ENGINES[activeIndex] : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Tabs
        value={activeIndex >= 0 ? activeIndex : 0}
        onChange={(_, index) => {
          const engine = ENGINES[index];
          navigate({ to: `${basePath}${engine.path}` });
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          minHeight: 40,
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0.5,
            textTransform: 'none',
            fontSize: 13,
          },
        }}
      >
        {ENGINES.map((engine) => (
          <Tab
            key={engine.path}
            icon={engine.icon}
            iconPosition="start"
            label={engine.label}
          />
        ))}
      </Tabs>

      {activeEngine?.standaloneRoute && (
        <MuiLink
          component={Link}
          to={activeEngine.standaloneRoute}
          underline="hover"
          sx={{ px: 2, whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          <Typography variant="caption" color="text.secondary">
            View all &rarr;
          </Typography>
        </MuiLink>
      )}
    </Box>
  );
}
